import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

import styles from "./DeliveryFormModal.module.scss";
import { IDeliveryFormInput, OptionsProps } from "../../types/orders";
import Select from "react-select";
import {
  getEditDelivery,
  toggleDeliveryModal,
} from "../../features/orders/ordersSlice";
import { selectStyles } from "../../static/styles";
import { FaCalendarAlt } from "react-icons/fa";
import moment from "moment";

type Props = {
  handleSaveDelivery: (data: IDeliveryFormInput) => void;
};

const DeliveryFormModal = ({ handleSaveDelivery }: Props) => {
  const dispatch = useAppDispatch();
  const editDelivery = useAppSelector(getEditDelivery);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [statusOption] = useState<OptionsProps[]>([
    {
      value: 0,
      label: "In Progress",
    },
    {
      value: 1,
      label: "Completed",
    },
  ]);

  const {
    handleSubmit,
    register,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm<IDeliveryFormInput>();

  const handleOnSubmit: SubmitHandler<IDeliveryFormInput> = async (data) => {
    setIsLoading(true);
    await handleSaveDelivery(data);
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(toggleDeliveryModal(false));
  };

  useEffect(() => {
    if (editDelivery && editDelivery.id) {
      reset({
        qty: editDelivery.qty,
        created_at: moment(editDelivery.created_at).format("YYYY-MM-DDTHH:mm"),
        status: editDelivery.order?.status ?? 0,
      });
    } else {
      reset();
    }
  }, [editDelivery]);

  return (
    <div className={styles.DeliveryFormModal}>
      <div className="relative h-[100vh] w-[100vw] overflow-y-scroll lg:overflow-auto top-0 lg:top-10 lg:mx-auto p-5 border lg:w-96 lg:h-fit shadow-lg lg:rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-2xl text-center font-bold leading-6 text-primary-1">
            Delivery Information
          </h3>
          <div className="mt-2 py-3">
            {editDelivery && editDelivery.order && (
              <div className="mb-4 p-3 bg-gray-50 rounded">
                <p className="text-sm text-gray-600">
                  <strong>Order ID:</strong> {editDelivery.order_id}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Party:</strong> {editDelivery.order.party?.firm_name}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Item:</strong>{" "}
                  {editDelivery.order.name ? `${editDelivery.order.name} - ` : ""}
                  {editDelivery.order.item?.name}
                  {editDelivery.order.inch > 1 && ` ${editDelivery.order.inch} Inch`}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Process:</strong> {editDelivery.order.process?.name}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Order Qty:</strong> {editDelivery.order.qty}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Order Date:</strong>{" "}
                  {moment(editDelivery.order.created_at).format("DD-MM-YYYY hh:mm A")}
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit(handleOnSubmit)}>
              <div className="grid gap-4">
                <div className="mt-1">
                  <label className="text-primary-1 font-bold">
                    Quantity :
                  </label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-14 w-full relative rounded">
                    <input
                      className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                      type="number"
                      pattern="[0-9]*"
                      placeholder="Enter quantity"
                      {...register("qty", { required: true, min: 1 })}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          function (e) {
                            e.preventDefault();
                          },
                          { passive: false }
                        )
                      }
                    />
                  </div>
                  {errors.qty && errors.qty.type === "required" && (
                    <span className="text-red-500">Enter quantity</span>
                  )}
                  {errors.qty && errors.qty.type === "min" && (
                    <span className="text-red-500">Quantity must be at least 1</span>
                  )}
                </div>

                <div className="mt-1">
                  <label className="text-primary-1 font-bold">
                    Status :
                  </label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-12 w-full relative rounded">
                    <Controller
                      {...register("status")}
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          value={
                            statusOption.find(
                              (val) => val.value === field.value
                            ) || null
                          }
                          className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          styles={selectStyles}
                          placeholder={"Select status"}
                          options={statusOption}
                          onChange={(val: any) =>
                            setValue("status", val?.value)
                          }
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="mt-1">
                  <label className="text-primary-1 font-bold">
                    Date :
                  </label>
                  <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded pr-[2px] h-[44px]">
                    <FaCalendarAlt className="text-white mx-2" />
                    <input
                      className="bg-white text-primary-1 placeholder:text-primary-2 font-semibold text-xl focus:outline-none p-2 rounded flex-grow h-[40px]"
                      type="datetime-local"
                      {...register("created_at", { required: true })}
                      placeholder="Delivery Date"
                    />
                  </div>

                  {errors?.created_at && (
                    <span className="text-red-500">
                      Select delivery date
                    </span>
                  )}
                </div>

                <div className="mt-3 text-center">
                  <button
                    className="mx-auto shadow disabled:opacity-50 rounded-3xl text-3xl lg:text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? "Please wait..." : "Continue"}
                  </button>
                </div>

                <div className="mt-1 text-center">
                  <button
                    className="mx-auto shadow disabled:opacity-50 rounded-3xl text-3xl lg:text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                    type="button"
                    onClick={handleCloseFormModal}
                    disabled={isLoading}
                  >
                    Close
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryFormModal;
